import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/site/PageHero";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, CalendarDays, Users, Sparkles, Check } from "lucide-react";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setD(null);
    api.get(`/destinations/${slug}`).then((r) => setD(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: d ? `${d.name} | Maharashtra DMC | Maha Sampada` : "Destination | Maha Sampada",
    description: d ? `${d.name}: ${d.tagline}. Customized B2B itineraries, ground handling and local expertise from Maha Sampada.` : "",
  });

  if (notFound) return <NotFoundBlock title="Destination Not Found" message="This Maharashtra region isn't available. Explore all our destinations instead." backTo="/destinations" backLabel="View All Destinations" />;
  if (!d) return <div className="flex min-h-[60vh] items-center justify-center pt-20 font-accent">Loading…</div>;

  return (
    <div>
      <PageHero overline="Destination Region" title={d.name} subtitle={d.tagline} image={d.image}
        crumbs={[{ label: "Destinations", to: "/destinations" }, { label: d.name }]} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="overline text-saffron">Overview</div>
            <p className="mt-4 text-lg leading-relaxed text-charcoal">{d.intro}</p>

            <div className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-charcoal"><MapPin className="h-5 w-5 text-saffron" />Key Places to Visit</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {d.places?.map((p) => (
                  <div key={p} className="flex items-center gap-2 rounded-xl border border-gold/25 bg-white px-4 py-3 text-sm text-charcoal">
                    <Check className="h-4 w-4 text-forest" />{p}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-charcoal"><Sparkles className="h-5 w-5 text-saffron" />Signature Experiences</h2>
              <ul className="mt-5 space-y-2">
                {d.experiences?.map((e) => (
                  <li key={e} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 shrink-0 text-forest" />{e}</li>
                ))}
              </ul>
            </div>

            {d.sample_itineraries?.length > 0 && (
              <div className="mt-12">
                <h2 className="font-display text-2xl font-semibold text-charcoal">Sample Itinerary Suggestions</h2>
                <div className="mt-4 flex flex-wrap gap-3">
                  {d.sample_itineraries.map((s) => (
                    <Link key={s} to="/itineraries" className="rounded-full border border-saffron/40 bg-saffron/5 px-4 py-2 font-accent text-xs text-saffron hover:bg-saffron/10">{s}</Link>
                  ))}
                </div>
              </div>
            )}

            {d.related_experiences?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-charcoal">Related Experiences</h2>
                <div className="mt-4 flex flex-wrap gap-3">
                  {d.related_experiences.map((s) => (
                    <span key={s} className="rounded-full border border-gold/30 bg-white px-4 py-2 font-accent text-xs text-charcoal">{s}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-gold/25 bg-white p-7">
              <h3 className="font-display text-xl font-semibold text-charcoal">Trip Snapshot</h3>
              <div className="mt-5 space-y-4 text-sm">
                <div className="flex items-start gap-3"><Clock className="mt-0.5 h-4 w-4 text-forest" /><div><div className="font-semibold text-charcoal">Recommended Duration</div><div className="text-muted-foreground">{d.duration}</div></div></div>
                <div className="flex items-start gap-3"><CalendarDays className="mt-0.5 h-4 w-4 text-forest" /><div><div className="font-semibold text-charcoal">Best Time to Visit</div><div className="text-muted-foreground">{d.best_time}</div></div></div>
                <div className="flex items-start gap-3"><Users className="mt-0.5 h-4 w-4 text-forest" /><div><div className="font-semibold text-charcoal">Ideal Travellers</div><div className="text-muted-foreground">{d.traveller_profiles?.join(", ")}</div></div></div>
              </div>
              <Link to={`/enquiry?ref=${encodeURIComponent(d.name)}`} className="mt-6 block">
                <Button data-testid="destination-enquiry-cta" className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">Request a Customized Itinerary</Button>
              </Link>
              <p className="mt-3 text-center text-[11px] text-muted-foreground">Confidential B2B rates shared on enquiry. No public prices.</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
